import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

/**
 * AppConfigService
 *
 * Loads runtime configuration from /_config/config.json (lives outside the
 * dist/ folder on the server, so FTP deploys never overwrite it).
 * Falls back to /assets/config.json for local `ng serve` development.
 *
 * Uses native fetch() instead of HttpClient so that the load happens
 * completely outside Angular's HTTP interceptor chain — no error snackbars,
 * no JWT headers, no console noise for the expected dev-time 404.
 *
 * Registered as an APP_INITIALIZER in app.config.ts so config is fully
 * loaded before any service or component is created.
 *
 * Resolution priority for get(key):
 *   1. /_config/config.json  (production — per-server file)
 *   2. /assets/config.json   (dev fallback — served by ng serve)
 *   3. environment.*         (build-time constant in environment.ts)
 *   4. provided fallback     (caller-supplied default)
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private _config: Record<string, any> = {};

  /**
   * Called by APP_INITIALIZER. Uses fetch() to stay outside the Angular
   * HTTP interceptor chain. Tries /_config/config.json first; silently
   * falls back to /assets/config.json if that returns a non-OK status.
   */
  load(): Promise<void> {
    const tryFetch = (url: string): Promise<Record<string, any>> =>
      fetch(url).then(r => {
        if (!r.ok) { throw new Error(`${r.status}`); }
        return r.json();
      });

    return tryFetch('/_config/config.json')
      .catch(() => tryFetch('/assets/config.json'))
      .then(data => { this._config = data ?? {}; })
      .catch(() => { /* both URLs failed — boot with empty config */ });
  }

  /**
   * Returns the value for key, with cascading fallbacks:
   * config.json → environment.ts → caller-provided fallback.
   */
  get<T = any>(key: string, fallback?: T): T {
    const fromConfig = this._config[key];
    if (fromConfig !== undefined) { return fromConfig as T; }
    const fromEnv = (environment as any)[key];
    if (fromEnv !== undefined) { return fromEnv as T; }
    return fallback as T;
  }

  /**
   * Convenience: full config snapshot (read-only view).
   * Useful for services that spread/merge the config object.
   */
  get snapshot(): Readonly<Record<string, any>> {
    return this._config;
  }
}
